// W36: thin wrapper over the generated identity client.
//
// Covers auth (signup/login/2FA/refresh/logout), password recovery + email
// verification, the profile / organisation / notification-preference edits and
// the tenant API-key CRUD served by `services/identity`.
//
// Types come from `./generated/identity` (regenerate with
// `pnpm --filter @repletoryreach/sdk-api generate identity`); the request path
// stays on the house core so the envelope, cursor pagination, Idempotency-Key,
// 401→refresh→replay and ApiError mapping behave exactly as before.
//
// The W36 cutover surfaced a fair amount of drift between this client and the
// service. W46 fixed the service side of it: `GET /v1/users` exists, refresh
// carries a `user`, `createApiKey` carries a `row`, `updateOrganisation`
// carries a `slug` - so the `⚠ DRIFT` notes that remain below are the ones
// that are still true of the running service: fields this client declares that
// no endpoint returns, and shapes that are deliberately hand-written because
// the generated type cannot express them. Each says which. See
// `_memory/2026-09-02-w46-contract-reconciliation-contacts-identity.md`.

import { http } from './_core/request.js';
import type { Envelope } from './_core/envelope.js';
import type { Page, PageOpts } from './_core/pagination.js';
import { buildPageQuery } from './_core/pagination.js';
import type * as gen from './generated/identity/index.js';

// ── Identity + organisation (generated) ────────────────────────────────────

/**
 * The organisation as `GET /v1/me` projects it. Note this is NOT the shape
 * `PATCH /v1/organisations/me` answers with; that one is
 * {@link OrganisationUpdated}.
 */
export type Organisation = gen.MeOrganisation & {
  /** When the account closes, if an owner has asked for it to be closed (2 October 2026). */
  closes_at?: string | null;
};

/**
 * A person's role in their organisation (2 October 2026). Agents work the
 * inbox and the contact book; owners and admins also handle money, numbers,
 * channels, the team, keys and settings; only an owner makes or changes an
 * owner. Any route an agent may not use answers 403 `ADMIN_ROLE_REQUIRED`.
 */
export const ORG_ROLES = ['owner', 'admin', 'agent'] as const;
export type OrgRole = (typeof ORG_ROLES)[number];

/** True for an owner or an admin. */
export function isOrgAdmin(role: string | null | undefined): boolean {
  return role === 'owner' || role === 'admin';
}

export type Me = gen.MeResponse & {
  /** owner, admin or agent; absent for platform staff. An operator looking in reads as admin. */
  role?: OrgRole | null;
};

/**
 * `PATCH /v1/organisations/me`. W46 added `slug` to the service's answer, so
 * this is now field-for-field the same shape as {@link Organisation}: the
 * console re-renders the org header straight from the update result.
 */
export type OrganisationUpdated = gen.OrganisationUpdatedResponse & { slug: string };

// ── Auth (generated, with the drifting fields pinned) ──────────────────────

/**
 * The user reference carried by a successful auth response. Built from the
 * generated `SignupUserRef`; `scope` is added because login and
 * `login/2fa` answer with `gen.SessionUserRef` (which has it) while signup
 * answers with `gen.SignupUserRef` (which does not).
 */
export type AuthUserRef = gen.SignupUserRef & {
  /** Present on `POST /v1/auth/login` and `/login/2fa`; absent from signup. */
  scope?: string;
  /**
   * ⚠ DRIFT: declared by this client but returned by no auth endpoint:
   * signup sends `{id, organisation_id}`, login and 2FA send
   * `{id, organisation_id, scope}`. Always `undefined` at runtime; kept
   * optional so existing reads keep compiling.
   */
  email?: string;
};

/**
 * The success payload shared by signup, login and `login/2fa`. Each endpoint
 * has its own generated response type (`gen.SignupResponse`,
 * `gen.LoginResponse`, `gen.SessionResponse`); this is their common superset,
 * which is why the two fields below are optional.
 */
export interface AuthResult {
  user: AuthUserRef;
  token: string;
  refresh_token: string;
  /**
   * ⚠ DRIFT: only `POST /v1/auth/exchange-api-key`
   * (`gen.ApiKeyExchangeResponse`) returns this. Signup, login and 2FA never
   * do, so it is always `undefined` on an {@link AuthResult}.
   */
  expires_in_seconds?: number;
}

/** Login response when the account has 2FA enabled. */
export interface TwoFactorChallenge {
  requires_2fa: true;
  pending_token: string;
}

/**
 * ⚠ DRIFT: kept hand-written rather than mapped to `gen.LoginResponse`.
 * `POST /v1/auth/login` really does emit two different bodies
 * (`{requires_2fa, pending_token}` or `{user, token, refresh_token}`), but the
 * service declares a single flat `LoginResponse` record with every field
 * nullable, so the generated type cannot discriminate and would widen
 * `token`/`refresh_token` to `string | null` for every caller. The union below
 * matches the wire far better and lets {@link isTwoFactorChallenge} narrow.
 */
export type LoginResult = AuthResult | TwoFactorChallenge | TwoFactorSetupChallenge;

export function isTwoFactorChallenge(r: LoginResult): r is TwoFactorChallenge {
  return (r as TwoFactorChallenge).requires_2fa === true;
}

/**
 * Login response when the account must set up two-factor sign-in before its first session
 * (platform staff invited from the console, 3 October 2026). The token opens only
 * {@link loginTwoFactorSetup} and {@link confirmLoginTwoFactorSetup}, for 15 minutes.
 */
export interface TwoFactorSetupChallenge {
  requires_2fa_setup: true;
  setup_token: string;
}

export function isTwoFactorSetupChallenge(r: LoginResult): r is TwoFactorSetupChallenge {
  return (r as TwoFactorSetupChallenge).requires_2fa_setup === true;
}

/** A session from setting up two-factor at sign-in, with the recovery codes shown once. */
export type TwoFactorSetupSession = AuthResult & { recovery_codes: string[] };

/**
 * `POST /v1/auth/refresh`. W46 added `user`, so a console restoring a session
 * from a stored refresh token learns who it signed in without a second
 * `GET /v1/me`.
 */
export type TokenPair = gen.TokenPairResponse & {
  user: { id: string; organisation_id: string; scope: string };
};

/**
 * `country_iso` is finally read (W47): the drift note that stood here was
 * true from Phase 6 until 10 September 2026, when the handler that ignored it
 * was opened to carry a referral code and set the organisation's country at
 * the same time. Two letters, upper-cased; anything else is ignored rather
 * than refused.
 *
 * `referral_code` is a partner's code, and `referral_source` says whether the
 * person typed it (`code_at_signup`) or arrived on a link that filled it in
 * (`link`). Identity checks the shape and passes it on; whether it belongs to
 * an active partner is billing's to answer, so a code nobody holds costs the
 * person signing up nothing.
 */
export type SignupRequest = gen.SignupRequest;

export type LoginRequest = Omit<gen.LoginRequest, 'domain'> & {
  /**
   * Sign-in domain. 'tenant' = tenant console; 'platform' = admin console.
   * The identity service refuses cross-domain attempts with HTTP 403.
   * When omitted the server treats the request as `tenant`.
   * (Narrowed from the generated `string | null`; those are the only two
   * values `LoginHandler` accepts.)
   */
  domain?: 'tenant' | 'platform';
};

export type RefreshRequest = gen.RefreshRequest;

// ── API keys (generated) ───────────────────────────────────────────────────

export type ApiKey = gen.ApiKeyListItem & {
  /**
   * ⚠ DRIFT: `GET /v1/api-keys` projects
   * `id, name, prefix, scopes, rate_limit_per_minute, is_active,
   * last_used_at, revoked_at, created_at`; it never selects `expires_at`, so
   * this is always absent. (`last_used_at` and `revoked_at` are returned but
   * were previously undeclared; they now come from the generated type.)
   */
  expires_at?: string | null;
  /** The most the key may spend on AI in a South African day, in minor units (14 October 2026). Null: no limit. */
  daily_ai_limit_minor?: number | null;
};

/**
 * The scopes a key may hold (2 October 2026). Identity refuses any other name
 * with 400 `UNKNOWN_SCOPE`, and a key with none with 400 `SCOPES_REQUIRED`.
 * `broadcasts:write` includes `broadcasts:read`. A key is sent as itself in the
 * `X-Api-Key` header; it is no longer exchanged for a token.
 */
export const API_SCOPES = [
  'messages:send',
  'messages:read',
  'contacts:read',
  'contacts:write',
  'templates:read',
  'broadcasts:read',
  'broadcasts:write',
  'media',
  'webhooks',
  'bookings:read',
  'bookings:write',
  'tickets:read',
  'tickets:write',
  // The AI API (14 October 2026), charged to the business's credits.
  'ai'
] as const;
export type ApiScope = (typeof API_SCOPES)[number];

export type CreateApiKeyRequest = Omit<gen.CreateApiKeyRequest, 'scopes'> & {
  /** At least one, from {@link API_SCOPES}. */
  scopes: ApiScope[] | string[] | null;
  /** The most the key may spend on AI in a South African day, in minor units (14 October 2026). Omit for no limit. */
  daily_ai_limit_minor?: number | null;
};

/**
 * `POST /v1/api-keys` 201: the flat fields (including the one and only sight
 * of the plaintext `key`) plus `row`, the same projection `GET /v1/api-keys`
 * returns, so a caller can splice the new key into a list it is already
 * showing without re-fetching. (W46 added `row`; it used to be `undefined`.)
 */
export type CreateApiKeyResult = gen.ApiKeyCreated & {
  row: ApiKey;
};

// ── Users, profile, prefs (generated) ──────────────────────────────────────

/** Note: `password` is required`POST /v1/users` binds `CreateUserRequest(Email, Password, DisplayName?)`. */
export type CreateUserRequest = gen.CreateUserRequest & {
  /** agent when omitted. Only an owner may invite an owner (403 `OWNER_ROLE_REQUIRED`). */
  role?: OrgRole;
};

export type CreatedUserRef = gen.CreatedUserRef;

/**
 * ⚠ DRIFT: `POST /v1/users` answers 201 with
 * `{user: {id, organisation_id, email}, token, refresh_token}` (it mints a
 * session for the new user), not the {@link User} row this module used to
 * declare.
 */
export type CreateUserResult = gen.CreateUserResponse;

export type UpdateProfileRequest = gen.UpdateProfileRequest;
export type ProfileUpdated = gen.ProfileUpdatedResponse;
export type UpdateOrganisationRequest = gen.UpdateOrganisationRequest;
export type LocaleResult = gen.LocaleResponse;
export type CountryResult = gen.CountryResponse;
/**
 * One notification preference.
 *
 * The column used to be a flat map of booleans, so a tenant's choice of email,
 * WhatsApp, webhook or in-app had nowhere to live and nothing read it. A value
 * is now either a plain on/off, or a map naming the channels it may go out on.
 *
 * Defaults are permissive on purpose: a key that is absent, and a channel
 * absent from a channel map, both mean "yes". An alert that stops a tenant's
 * sending must not be silently off because they never opened the page.
 */
export type NotificationPrefValue = boolean | Record<string, boolean>;

/**
 * ⚠ DRIFT: the generated type is `Record<string, boolean>`, taken from the old
 * closed shape of the endpoint. The service now accepts and returns the wider
 * value above, so the map is hand-written here rather than derived.
 */
export type NotificationPrefs = Record<string, NotificationPrefValue>;

/** The vocabulary `GET /v1/users/me/notification-prefs` returns beside the values. */
export interface NotificationPrefsMeta {
  /** Every notification kind the platform sends, so a console renders the editor from the server. */
  kinds: string[];
  /** `email`, `whatsapp`, `webhook`, `in_app`. */
  channels: string[];
}

/** The full answer: the stored values plus the vocabulary to render them. */
export interface NotificationPrefsResult {
  data: NotificationPrefs;
  meta?: NotificationPrefsMeta;
}

// ── Recovery + verification + 2FA (generated) ──────────────────────────────

export type SentResult = gen.SentResponse;
export type PasswordReset = gen.PasswordResetResponse;
export type PasswordChanged = gen.PasswordChangedResponse;
export type EmailVerified = gen.EmailVerifiedResponse;

/** ⚠ DRIFT, `secret` is nullable: the service omits it when 2FA is already set up. */
export type TwoFactorSetup = gen.TwoFactorSetupResponse;

export type TwoFactorEnabled = gen.TwoFactorEnabledResponse;
export type TwoFactorDisabled = gen.TwoFactorDisabledResponse;
export type RecoveryCodes = gen.RecoveryCodesResponse;

// ── Passwords ──────────────────────────────────────────────────────────────

/**
 * Identity's password rule since 2 October 2026: at least 12 characters, no
 * rule about capitals, digits or symbols, and none of the most common leaked
 * passwords. A password that already works keeps working until it is changed.
 */
export const PASSWORD_MIN_LENGTH = 12;

/**
 * Why identity refused a new password (sign-up, reset, change, an invitation,
 * adding one to a social account), from the refusal's `error.reason`:
 * `breached` (a common leaked password) or `too_short`. Null for any other
 * refusal. The error codes themselves are unchanged.
 */
export type PasswordRefusalReason = 'breached' | 'too_short';
export function passwordRefusalReason(err: unknown): PasswordRefusalReason | null {
  const body = (err as { body?: { error?: { reason?: unknown } } } | null)?.body;
  const reason = body?.error?.reason;
  return reason === 'breached' || reason === 'too_short' ? reason : null;
}

// ── Operations ─────────────────────────────────────────────────────────────

export function signup(req: SignupRequest): Promise<Envelope<AuthResult>> {
  return http.post<Envelope<AuthResult>>('/v1/auth/signup', { body: req, auth: false });
}

export function login(req: LoginRequest): Promise<Envelope<LoginResult>> {
  return http.post<Envelope<LoginResult>>('/v1/auth/login', { body: req, auth: false });
}

/** Complete a 2FA login challenge with a TOTP or recovery code. */
export function loginTwoFactor(pending_token: string, code: string): Promise<Envelope<AuthResult>> {
  return http.post<Envelope<AuthResult>>('/v1/auth/login/2fa', {
    body: { pending_token, code },
    auth: false
  });
}

/** The authenticator secret for an account setting up two-factor at sign-in. */
export function loginTwoFactorSetup(setup_token: string): Promise<Envelope<{ secret: string; otpauth_uri: string }>> {
  return http.post<Envelope<{ secret: string; otpauth_uri: string }>>('/v1/auth/login/2fa-setup', { body: { setup_token }, auth: false });
}

/** The first code: switches two-factor on and signs in. 400 `INVALID_CODE`; five wrong codes void the token (401). */
export function confirmLoginTwoFactorSetup(setup_token: string, code: string): Promise<Envelope<TwoFactorSetupSession>> {
  return http.post<Envelope<TwoFactorSetupSession>>('/v1/auth/login/2fa-setup/confirm', { body: { setup_token, code }, auth: false });
}

/** Rotates the refresh token: the one passed in is spent. See {@link TokenPair}. */
export function refresh(req: RefreshRequest): Promise<Envelope<TokenPair>> {
  return http.post<Envelope<TokenPair>>('/v1/auth/refresh', { body: req, auth: false });
}

export function logout(refresh_token?: string): Promise<void> {
  return http.post<void>('/v1/auth/logout', { body: { refresh_token: refresh_token ?? null } });
}

// ── Password recovery + email verification ───────────────────────────────

export function forgotPassword(email: string): Promise<Envelope<SentResult>> {
  return http.post<Envelope<SentResult>>('/v1/auth/forgot-password', { body: { email }, auth: false });
}

export function resetPassword(email: string, token: string, new_password: string): Promise<Envelope<PasswordReset>> {
  return http.post<Envelope<PasswordReset>>('/v1/auth/reset-password', {
    body: { email, token, new_password },
    auth: false
  });
}

export function changePassword(current_password: string, new_password: string): Promise<Envelope<PasswordChanged>> {
  return http.post<Envelope<PasswordChanged>>('/v1/auth/change-password', {
    body: { current_password, new_password }
  });
}

export function verifyEmail(email: string, code: string): Promise<Envelope<EmailVerified>> {
  return http.post<Envelope<EmailVerified>>('/v1/auth/verify-email', { body: { email, code }, auth: false });
}

export function resendVerification(email: string): Promise<Envelope<SentResult>> {
  return http.post<Envelope<SentResult>>('/v1/auth/resend-verification', { body: { email }, auth: false });
}

// ── Two-factor auth ───────────────────────────────────────────────────────

export function twoFactorSetup(): Promise<Envelope<TwoFactorSetup>> {
  return http.post<Envelope<TwoFactorSetup>>('/v1/auth/2fa/setup');
}

export function twoFactorEnable(code: string): Promise<Envelope<TwoFactorEnabled>> {
  return http.post<Envelope<TwoFactorEnabled>>('/v1/auth/2fa/enable', { body: { code } });
}

export function twoFactorDisable(code: string): Promise<Envelope<TwoFactorDisabled>> {
  return http.post<Envelope<TwoFactorDisabled>>('/v1/auth/2fa/disable', { body: { code } });
}

export function twoFactorRecoveryCodes(code: string): Promise<Envelope<RecoveryCodes>> {
  return http.post<Envelope<RecoveryCodes>>('/v1/auth/2fa/recovery-codes', { body: { code } });
}

// ── Profile + organisation + notification prefs ──────────────────────────

export function updateProfile(req: UpdateProfileRequest): Promise<Envelope<ProfileUpdated>> {
  return http.patch<Envelope<ProfileUpdated>>('/v1/users/me', { body: req });
}

// ── Export and closing the account (2 October 2026) ───────────────────────

export interface ExportLink {
  /** Relative to the API's base address. Opening it downloads a zip; it works for ten minutes. */
  url: string;
  expires_at: string;
}

/**
 * A ten-minute link to everything held for the organisation, one JSON file per
 * part of the platform, in a zip assembled while it downloads. Owners and
 * admins only (403 `ADMIN_ROLE_REQUIRED`). Making one is on the security log.
 */
export function createExportLink(): Promise<Envelope<ExportLink>> {
  return http.post<Envelope<ExportLink>>('/v1/organisations/me/export');
}

export interface CloseAccountRequest {
  /** The organisation's name, typed again. */
  confirm_name: string;
  reason?: string;
  /** Ask for unused bought credit back (the refunds policy: within 12 months of buying). */
  refund_unused_credits?: boolean;
}

export interface AccountClosure {
  /** When the account closes; null when it is not closing. */
  closes_at: string | null;
  close_requested_at: string | null;
  refund_requested: boolean;
  closed_at: string | null;
}

/**
 * Closes the account 30 days from now: the plan stops at the end of the paid
 * month, and on the day every part of the platform deletes the organisation's
 * data (financial records stay as the law requires) and the team's sign-ins
 * end. An owner only (403 `OWNER_ROLE_REQUIRED`, also for an operator looking
 * in); 400 `CONFIRMATION_MISMATCH` when the name does not match; 409
 * `ALREADY_CLOSING`.
 */
export function closeAccount(req: CloseAccountRequest): Promise<Envelope<AccountClosure>> {
  return http.post<Envelope<AccountClosure>>('/v1/organisations/me/close', { body: req });
}

/** Takes a closure back before the day. An owner only. */
export function keepAccount(): Promise<Envelope<AccountClosure>> {
  return http.delete<Envelope<AccountClosure>>('/v1/organisations/me/close');
}

// ── Agreements (2 October 2026) ────────────────────────────────────────────

/** One agreement as the organisation sees it: the current version, and whether it was accepted. */
export interface AgreementStatus {
  /** `dpa`: the data processing agreement. */
  kind: string;
  name: string;
  /** Where the wording is read on the website, such as `/legal/dpa`. */
  path: string;
  current_version: string;
  published_at: string;
  /** What changed in the current version, when it replaced an earlier one. Absent when empty. */
  summary?: string | null;
  /** True only when the CURRENT version was accepted. */
  accepted: boolean;
  accepted_version?: string | null;
  accepted_at?: string | null;
  accepted_by_email?: string | null;
  accepted_by_name?: string | null;
}

export interface AgreementAcceptance {
  kind: string;
  version: string;
  accepted_at: string;
  accepted_by_email: string | null;
  accepted_by_name: string | null;
}

/** The agreements this organisation must accept. The console is not usable until each is `accepted`. */
export function listAgreements(): Promise<Envelope<AgreementStatus[]>> {
  return http.get<Envelope<AgreementStatus[]>>('/v1/organisations/me/agreements');
}

/**
 * Accepts `version` of an agreement for the organisation, recording who, when
 * and from where. Fails with 409 `AGREEMENT_VERSION_CHANGED` (and the
 * `current_version`) when a newer one was published meanwhile, and 403 for an
 * operator looking in through impersonation. Accepting twice is harmless.
 */
export function acceptAgreement(kind: string, version: string): Promise<Envelope<AgreementAcceptance> & { created: boolean }> {
  return http.post<Envelope<AgreementAcceptance> & { created: boolean }>(
    `/v1/organisations/me/agreements/${encodeURIComponent(kind)}`, { body: { version } });
}

export function updateOrganisation(req: UpdateOrganisationRequest): Promise<Envelope<OrganisationUpdated>> {
  return http.patch<Envelope<OrganisationUpdated>>('/v1/organisations/me', { body: req });
}

// ── Push notifications (9 October 2026) ──────────────────────────────────────
//
// Firebase Cloud Messaging: a person switches notifications on per device. The console asks `getPushConfig` for
// the web app's public Firebase settings, gets a token from Firebase, and registers it here; billing sends to the
// tokens of whoever an alert is for. The owner's app later registers with platform `android` or `ios`.

export interface PushConfig {
  /** False until Firebase is set up under the platform's Settings, Integrations. */
  enabled: boolean;
  project_id?: string | null;
  api_key?: string | null;
  app_id?: string | null;
  messaging_sender_id?: string | null;
  /** The Web Push certificate's public key, for Firebase's getToken. */
  vapid_key?: string | null;
}

export type PushPlatform = 'web' | 'android' | 'ios';

export interface PushDevice {
  id: string;
  platform: PushPlatform;
  /** What the person sees in their list: "Chrome on Windows". */
  label?: string | null;
  created_at: string;
  last_seen_at: string;
}

export function getPushConfig(): Promise<Envelope<PushConfig>> {
  return http.get<Envelope<PushConfig>>('/v1/push/config');
}

export function listPushDevices(): Promise<Envelope<PushDevice[]>> {
  return http.get<Envelope<PushDevice[]>>('/v1/push/devices');
}

/** Receive push notifications on this device. A token another person registered moves to this one. */
export function registerPushDevice(req: { token: string; platform?: PushPlatform; label?: string }): Promise<Envelope<PushDevice>> {
  return http.post<Envelope<PushDevice>>('/v1/push/devices', { body: req });
}

export function removePushDevice(id: string): Promise<void> {
  return http.delete<void>(`/v1/push/devices/${encodeURIComponent(id)}`);
}

/** Stop push on this device by its token, for signing out. */
export function forgetPushDevice(token: string): Promise<void> {
  return http.post<void>('/v1/push/devices/forget', { body: { token } });
}

export function getNotificationPrefs(): Promise<NotificationPrefsResult> {
  return http.get<NotificationPrefsResult>('/v1/users/me/notification-prefs');
}

export function updateNotificationPrefs(prefs: NotificationPrefs): Promise<NotificationPrefsResult> {
  return http.put<NotificationPrefsResult>('/v1/users/me/notification-prefs', { body: prefs });
}

/**
 * Whether one notification may go out on one channel, with the service's own
 * default-on semantics. Both consoles read preferences through this rather
 * than reimplementing the rule and drifting from the dispatcher.
 */
export function notificationAllowed(prefs: NotificationPrefs | undefined, kind: string, channel: string): boolean {
  const value = prefs?.[kind];
  if (value === undefined) return true;
  if (typeof value === 'boolean') return value;
  const onChannel = value[channel];
  return onChannel === undefined ? true : onChannel === true;
}

// ── One-click unsubscribe ─────────────────────────────────────────────────
//
// The other end of the `List-Unsubscribe` header on every notification email.
// Both calls are anonymous by design: a mailbox provider follows the header
// with no cookie and no bearer token, and a person clicking a footer link in a
// message from three weeks ago is not signed in either. The token in the URL
// carries the whole authority (who, which notification, until when, HMAC'd),
// so nothing else is needed and nothing else is accepted.
//
// A transactional kind cannot be unsubscribed. The service refuses to mint a
// token naming one and refuses to verify one, so a verification code, a
// password reset, a receipt or a security notice has no path through here.
// See `services/identity/Endpoints/UnsubscribeEndpoints.cs`.

/** What one of these calls did. */
export interface UnsubscribeResult {
  /** The notification kind the token named, e.g. `low_balance`. */
  kind?: string;
  /** True once the address will no longer get that kind by email. */
  unsubscribed?: boolean;
}

/**
 * Stop sending one notification kind to whoever the token names. This is the
 * POST behind the `List-Unsubscribe` header and behind the button on the
 * landing page; a GET on the same URL only renders the page, so a mail scanner
 * following links cannot silently unsubscribe a customer.
 *
 * It never disables a transactional message: an expired or forged token, or
 * one naming a kind that must always arrive, is refused with 400.
 */
export function unsubscribe(token: string): Promise<UnsubscribeResult | string> {
  return http.post<UnsubscribeResult | string>(
    `/v1/auth/unsubscribe?t=${encodeURIComponent(token)}`,
    { auth: false }
  );
}

/** Undo an unsubscribe with the same token, which is what the landing page's second button does. */
export function resubscribe(token: string): Promise<UnsubscribeResult | string> {
  return http.post<UnsubscribeResult | string>(
    `/v1/auth/unsubscribe/resubscribe?t=${encodeURIComponent(token)}`,
    { auth: false }
  );
}

export function me(): Promise<Envelope<Me>> {
  return http.get<Envelope<Me>>('/v1/me');
}

/** ⚠ DRIFT; was declared `Promise<void>`; the service answers 200 `{data:{locale}}`. */
export function updateMyLocale(locale: string): Promise<Envelope<LocaleResult>> {
  return http.put<Envelope<LocaleResult>>('/v1/users/me/locale', { body: { locale } });
}

/** ⚠ DRIFT; was declared `Promise<void>`; the service answers 200 `{data:{country_iso}}`. */
export function updateOrgCountry(country_iso: string): Promise<Envelope<CountryResult>> {
  return http.put<Envelope<CountryResult>>('/v1/organisations/me/country', { body: { country_iso } });
}

/**
 * ⚠ DRIFT; was declared `Envelope<User>`. `POST /v1/users` answers 201 with a
 * freshly minted session for the created user; see {@link CreateUserResult}.
 */
export function createUser(req: CreateUserRequest): Promise<Envelope<CreateUserResult>> {
  return http.post<Envelope<CreateUserResult>>('/v1/users', { body: req });
}

export function listApiKeys(): Promise<Envelope<ApiKey[]>> {
  return http.get<Envelope<ApiKey[]>>('/v1/api-keys');
}

export function createApiKey(req: CreateApiKeyRequest): Promise<Envelope<CreateApiKeyResult>> {
  return http.post<Envelope<CreateApiKeyResult>>('/v1/api-keys', { body: req });
}

/** Sets a key's daily AI limit in minor units, or clears it with null (14 October 2026). */
export function setApiKeyAiLimit(id: string, dailyAiLimitMinor: number | null): Promise<Envelope<{ id: string; daily_ai_limit_minor: number | null }>> {
  return http.put<Envelope<{ id: string; daily_ai_limit_minor: number | null }>>(`/v1/api-keys/${encodeURIComponent(id)}/ai-limit`,
    { body: { daily_ai_limit_minor: dailyAiLimitMinor } });
}

export function revokeApiKey(id: string): Promise<void> {
  return http.delete<void>(`/v1/api-keys/${encodeURIComponent(id)}`);
}

// ── Users (W46) ────────────────────────────────────────────────────────────
// `GET /v1/users` lists the CALLER'S OWN organisation. Cross-tenant listing is
// `admin.listUsers()`, behind the platform-admin policy.

/** A teammate, as the tenant console sees them. */
export type User = gen.UserListItem;

export interface UserListOpts extends PageOpts {
  /** Substring match on email or display name. */
  search?: string;
  offset?: number;
}

export function listUsers(opts?: UserListOpts): Promise<Page<User>> {
  const query: Record<string, string | number | undefined> = { ...buildPageQuery(opts) };
  if (opts?.search !== undefined) query.search = opts.search;
  if (opts?.offset !== undefined) query.offset = opts.offset;
  return http.get<Page<User>>('/v1/users', { query });
}

/** Fields a tenant admin may change on a teammate. Omit one to leave it. */
export interface UpdateUserRequest {
  display_name?: string;
  locale?: string;
  /**
   * Change the person's role. Only an owner sets or changes an owner
   * (403 `OWNER_ROLE_REQUIRED`), and an organisation keeps at least one
   * active owner (409 `LAST_OWNER`).
   */
  role?: OrgRole;
  /** `false` suspends: the account and its history stay, sign-in stops. */
  is_active?: boolean;
}

/**
 * Updates a teammate, or suspends them.
 *
 * The service mapped no PATCH and no DELETE on users, so an ex-employee could
 * not be removed from the tenant console at all.
 */
export function updateUser(id: string, req: UpdateUserRequest): Promise<Envelope<gen.UserUpdateResult>> {
  return http.patch<Envelope<gen.UserUpdateResult>>(`/v1/users/${encodeURIComponent(id)}`, { body: req });
}

/**
 * Removes a teammate from the organisation.
 *
 * A soft removal: the row survives so every conversation, note and audit entry
 * that names them still resolves to a name rather than a dangling id. What
 * goes is the ability to sign in and their role membership.
 */
export function removeUser(id: string): Promise<void> {
  return http.delete<void>(`/v1/users/${encodeURIComponent(id)}`);
}

// ── Sessions and 2FA state ────────────────────────────────────────────────
// `auth_refresh_tokens` has recorded the user agent and creation time since
// the table shipped and nothing ever read it back, so the Security page had a
// section promising a sessions list with no endpoint behind it.

/** One active session. */
export type Session = gen.SessionDto;

/** Active sessions for the signed-in user, newest first. */
export function listSessions(): Promise<Envelope<Session[]>> {
  return http.get<Envelope<Session[]>>('/v1/auth/sessions');
}

/**
 * Ends one session.
 *
 * The only session control used to be "sign out everywhere", so noticing one
 * suspicious device meant signing yourself out as well.
 */
export function revokeSession(id: string): Promise<void> {
  return http.delete<void>(`/v1/auth/sessions/${encodeURIComponent(id)}`);
}

/**
 * Two-factor state, including how many recovery codes are left.
 *
 * A user who has burned eight of ten codes had no way to find out, and
 * regenerating requires a working authenticator: exactly what someone who has
 * run out of codes does not have.
 */
export function twoFactorStatus(): Promise<Envelope<gen.TwoFactorStatusDto>> {
  return http.get<Envelope<gen.TwoFactorStatusDto>>('/v1/auth/2fa/status');
}

// ── Social sign-in: Google, Facebook and Apple (2026-09-07) ───────────────
//
// These endpoints are hand-written rather than generated: two of them are
// browser redirects that answer 302 and carry no JSON body, so the OpenAPI
// document has nothing for the generator to build a client from.
//
// The shape of the flow matters more than the shape of the types. The console
// never handles a provider token. It sends the person to `startUrl(...)`, the
// provider returns them to identity, identity spends the authorisation code
// server-side, and what lands back in the console is a single-use handoff code
// in the address bar. {@link exchangeSocialCode} swaps that for the ordinary
// JWT pair. Access and refresh tokens therefore never appear in a URL, a
// browser history entry, a server log or a Referer header.

/** One row of `GET /v1/auth/providers`. No credential is carried. */
export interface AuthProvider {
  /** `google`, `facebook` or `apple`. Also the segment in the flow URLs. */
  provider: string;
  /** What to write on the button: "Google", "Facebook", "Apple". */
  display_name: string;
  enabled: boolean;
}

/**
 * The providers an operator has switched on under Settings then Integrations
 * in the platform console. A provider with no row, an incomplete row or a
 * disabled row is simply absent, so a sign-in page renders a button only where
 * pressing it would work.
 */
export function listAuthProviders(): Promise<Envelope<AuthProvider[]>> {
  return http.get<Envelope<AuthProvider[]>>('/v1/auth/providers', { auth: false });
}

/**
 * Where to send the browser to begin. This is a full-page navigation, not a
 * fetch: the provider will not answer an XHR and the redirect chain has to
 * happen in the address bar.
 *
 * @param apiBaseUrl the gateway's public origin, e.g. `https://api.repletoryreach.co.za`
 * @param returnTo   a RELATIVE path inside the console to land on afterwards.
 *                   A full URL is dropped by the service rather than followed,
 *                   so this can never become an open redirect.
 * @param domain     `tenant` (default) or `platform`.
 */
export function socialSignInUrl(
  apiBaseUrl: string,
  provider: string,
  opts: { returnTo?: string; domain?: 'tenant' | 'platform' } = {}
): string {
  const query = new URLSearchParams();
  if (opts.returnTo) query.set('return_to', opts.returnTo);
  if (opts.domain) query.set('domain', opts.domain);
  const qs = query.toString();
  return `${apiBaseUrl.replace(/\/$/, '')}/v1/auth/${encodeURIComponent(provider)}/start${qs ? `?${qs}` : ''}`;
}

/**
 * Spends the single-use code the callback left in the address bar and returns
 * the same answer the password path gives: the session, or (since 9 October
 * 2026) the two-factor challenge an account with an authenticator must pass
 * first at `/v1/auth/login/2fa`. The code is good for two minutes and for
 * exactly one call. `provider` is the one named in the callback's address,
 * for the sign-in record.
 */
export function exchangeSocialCode(code: string, provider?: string): Promise<Envelope<LoginResult>> {
  return http.post<Envelope<LoginResult>>('/v1/auth/social/exchange', { body: provider ? { code, provider } : { code }, auth: false });
}

/** `GET /v1/auth/methods`: how the signed-in account can get back in. */
export interface SignInMethods {
  /** False for somebody who signed up through a provider and never set one. */
  has_password: boolean;
  /** The providers attached to this account. */
  providers: string[];
}

export function getSignInMethods(): Promise<Envelope<SignInMethods>> {
  return http.get<Envelope<SignInMethods>>('/v1/auth/methods');
}

/**
 * Sets a first password on an account created through a provider, so one
 * provider is never a single point of failure for getting in. Refused with
 * `PASSWORD_ALREADY_SET` when the account has one; use
 * {@link changePassword} then.
 */
export function setPassword(new_password: string): Promise<Envelope<PasswordChanged>> {
  return http.post<Envelope<PasswordChanged>>('/v1/auth/password/set', { body: { new_password } });
}

/**
 * Detaches a provider. Refused with `LAST_SIGN_IN_METHOD` when it is the only
 * way the person can sign in, because no confirmation dialog makes locking
 * somebody out of their own account a good outcome.
 */
export function unlinkAuthProvider(provider: string): Promise<void> {
  return http.delete<void>(`/v1/auth/providers/${encodeURIComponent(provider)}`);
}

/**
 * The refusals a social callback can end in. Identity redirects back to
 * `<console>/auth/callback?error=<code>`, so the console reads these from the
 * query string rather than from a response body.
 */
export type SocialSignInError =
  /** The state was unknown, expired, replayed, or did not match the browser that started the flow. */
  | 'STATE_INVALID'
  /** The person pressed Cancel at the provider. */
  | 'PROVIDER_REFUSED'
  /** The provider was switched off between starting and finishing. */
  | 'PROVIDER_DISABLED'
  | 'NO_CODE'
  | 'TOKEN_EXCHANGE_FAILED'
  | 'NO_ID_TOKEN'
  /** The identity token failed signature, issuer, audience or lifetime validation. */
  | 'ID_TOKEN_INVALID'
  /** The identity token did not carry the nonce this flow started with. */
  | 'NONCE_MISMATCH'
  | 'PROVIDER_UNREACHABLE'
  /** The provider gave no email address (a Facebook account with none). */
  | 'EMAIL_REQUIRED'
  /** The provider has the address but has not confirmed it. */
  | 'EMAIL_NOT_VERIFIED'
  /** The operator limited this provider to certain email domains. */
  | 'DOMAIN_NOT_ALLOWED'
  /** An account holds this address but has never verified it, so linking would be an account-takeover path. */
  | 'LINK_REQUIRES_VERIFICATION'
  /** A customer account tried the platform console, or the reverse. */
  | 'WRONG_DOMAIN'
  | 'ACCOUNT_SUSPENDED'
  /** No platform account exists for this address, and a callback may never create one. */
  | 'PLATFORM_SIGNUP_BLOCKED'
  | 'SIGNUP_FAILED'
  | 'SIGN_IN_FAILED';

// ── Plan allowances ───────────────────────────────────────────────────────

/**
 * What the organisation's plan allows for one limit, and what it is using.
 * Billing owns the subscription so it answers the allowance; the service that
 * owns the rows counts them. Identity carries the `team_members` one in
 * `GET /v1/users` meta.
 */
export interface PlanAllowance {
  /** `numbers`, `team_members`, `flows` or `knowledge_documents`. */
  limit: string;
  /** Absent or null when the plan is unlimited for this key. */
  allowance?: number | null;
  unlimited: boolean;
  /** How many the organisation is using right now. */
  current: number;
  plan_code?: string | null;
  plan_name?: string | null;
}

/**
 * The HTTP 409 body every service returns when a plan allowance is reached, so
 * a console can render "You are on Starter, which includes 1 number. Upgrade to
 * add more." rather than a status code.
 */
export interface PlanLimitReached {
  code: 'PLAN_LIMIT_REACHED';
  message: string;
  limit: string;
  allowance?: number | null;
  current: number;
  plan_code?: string | null;
  plan_name?: string | null;
}

/** True when `err` is a plan-allowance refusal, narrowing `err.body.error` to {@link PlanLimitReached}. */
export function isPlanLimitReached(err: unknown): err is { status: 409; body: { error: PlanLimitReached } } {
  const e = err as { status?: number; body?: { error?: { code?: unknown } } } | null;
  return !!e && e.status === 409 && e.body?.error?.code === 'PLAN_LIMIT_REACHED';
}

// ── Security events ───────────────────────────────────────────────────────
//
// Hand-written rather than generated: `security_events` shipped after the last
// regeneration of `./generated/identity`. Run
// `pnpm --filter @repletoryreach/sdk-api generate identity` once the parallel
// service work settles and these can drop to `gen.SecurityEventDto`.

/** A code from `lookup_security_event_types`. New codes reach the wire before this union does, so read it as an open set. */
export type SecurityEventType =
  | 'sign_in'
  | 'sign_in_failed'
  | 'sign_out'
  | 'password_set'
  | 'password_changed'
  | 'password_reset_requested'
  | 'password_reset_completed'
  | 'mfa_enabled'
  | 'mfa_disabled'
  | 'mfa_challenge_failed'
  | 'recovery_codes_regenerated'
  | 'email_verified'
  | 'provider_linked'
  | 'provider_unlinked'
  | 'api_key_created'
  | 'api_key_revoked'
  | 'impersonation_started'
  | (string & {});

/**
 * One security-relevant moment on an account.
 *
 * `user_id` is null on exactly one kind of row: a sign-in attempt against an
 * address no account holds, where `user_email` carries the address that was
 * tried. `actor_user_id` and `actor_email` appear only on the platform-admin
 * feed, and only when somebody other than the subject did it.
 */
export interface SecurityEvent {
  id: string;
  user_id?: string | null;
  /** The account's address, or the address an attempt was aimed at. */
  user_email?: string | null;
  organisation_id?: string | null;
  event_type: SecurityEventType;
  /** `info`, `notice` or `warning`, from the lookup row: colour a row from this rather than from a list of codes. */
  severity: 'info' | 'notice' | 'warning' | (string & {});
  outcome: 'success' | 'failure';
  /** The caller's address as the gateway saw it. */
  ip?: string | null;
  /** A short reading of the user agent, e.g. "Chrome on macOS". */
  device?: string | null;
  user_agent?: string | null;
  detail?: string | null;
  /**
   * How many occurrences this row stands for. Repeated failures from one
   * address inside the aggregation window fold into a single row, so a
   * credential-stuffing run is a handful of rows with a high count rather than
   * a table nobody can read. 1 on every ordinary row.
   */
  repeat_count: number;
  /** Platform-admin feed only. */
  actor_user_id?: string | null;
  /** Platform-admin feed only. */
  actor_email?: string | null;
  occurred_at: string;
  /** The most recent occurrence folded in; equal to `occurred_at` when `repeat_count` is 1. */
  last_occurred_at: string;
}

export interface SecurityEventListMeta {
  count: number;
  total: number;
  limit: number;
  /** Pass back as `cursor` for the next page. Absent on the last one. */
  next_cursor?: string | null;
  /** How long rows are kept, so a quiet window is not read as a clean one. */
  retention_days: number;
}

export interface SecurityEventListOpts {
  /** Platform feed only. */
  user_id?: string;
  /** Platform feed only. */
  org_id?: string;
  /** One code, or several comma-separated. An unknown code narrows to nothing rather than being ignored. */
  event_type?: string;
  outcome?: 'success' | 'failure';
  since?: string;
  until?: string;
  limit?: number;
  cursor?: string;
}

/** One filter option, from the lookup table rather than a hard-coded list. */
export interface SecurityEventTypeOption {
  code: SecurityEventType;
  name: string;
  severity: string;
}

/** Every account's security events, newest first. Platform admin only. */
export function listSecurityEvents(
  opts: SecurityEventListOpts = {}
): Promise<Envelope<SecurityEvent[]> & { meta: SecurityEventListMeta }> {
  return http.get<Envelope<SecurityEvent[]> & { meta: SecurityEventListMeta }>(
    '/v1/admin/security-events',
    { query: securityQuery(opts) }
  );
}

/** The signed-in person's own security events. No actor column. */
export function listMySecurityEvents(
  opts: Omit<SecurityEventListOpts, 'user_id' | 'org_id'> = {}
): Promise<Envelope<SecurityEvent[]> & { meta: SecurityEventListMeta }> {
  return http.get<Envelope<SecurityEvent[]> & { meta: SecurityEventListMeta }>(
    '/v1/users/me/security-events',
    { query: securityQuery(opts) }
  );
}

/** The event types the feed can be filtered by. Platform admin only. */
export function listSecurityEventTypes(): Promise<Envelope<SecurityEventTypeOption[]>> {
  return http.get<Envelope<SecurityEventTypeOption[]>>('/v1/admin/security-events/types');
}

function securityQuery(opts: SecurityEventListOpts): Record<string, string | number> {
  const q: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(opts)) if (v !== undefined && v !== null && v !== '') q[k] = v as string | number;
  return q;
}

// ── API key provenance and batch revoke (platform admin) ──────────────────

/**
 * One key with everything known about where it came from and what has used it.
 *
 * `last_used_ip` and `use_count` come from `api_key_usage_logs` rather than
 * from columns on the key, so `use_count` is calls recorded in the log and not
 * a lifetime total if the log has been trimmed.
 */
export interface AdminApiKeyDetail {
  id: string;
  organisation_id: string;
  name: string;
  /** The first 12 characters, e.g. `rk_live_AbCd`. Never the key. */
  key_prefix: string;
  scopes: string[];
  rate_limit_per_minute: number;
  is_active: boolean;
  created_at: string;
  created_by_user_id?: string | null;
  created_by_email?: string | null;
  /** Null for every key minted before provenance was recorded. */
  created_ip?: string | null;
  last_used_at?: string | null;
  last_used_ip?: string | null;
  use_count: number;
  expires_at?: string | null;
  revoked_at?: string | null;
  revoked_by_user_id?: string | null;
  revoked_by_email?: string | null;
  revoked_reason?: string | null;
}

/**
 * What one batch revoke did. `already_revoked` is reported rather than
 * refused: the outcome the caller asked for holds for those keys.
 */
export interface AdminApiKeyRevokeResult {
  revoked: number;
  already_revoked: number;
  not_found: number;
  /** The ids this call actually turned off. */
  ids: string[];
}

/** One key with its provenance. Platform admin only. */
export function adminGetApiKey(id: string): Promise<Envelope<AdminApiKeyDetail>> {
  return http.get<Envelope<AdminApiKeyDetail>>(`/v1/admin/api-keys/${encodeURIComponent(id)}`);
}

/**
 * Revoke a whole selection in one transaction, so a partial failure cannot
 * leave half a selection live. 1 to 100 ids; `reason` is required and stored
 * on every row.
 */
export function adminRevokeApiKeys(
  ids: string[],
  reason: string
): Promise<Envelope<AdminApiKeyRevokeResult> & { meta?: { requested: number } }> {
  return http.post<Envelope<AdminApiKeyRevokeResult> & { meta?: { requested: number } }>(
    '/v1/admin/api-keys/revoke',
    { body: { ids, reason } }
  );
}

// ── Business hours and closed days (7 October 2026) ───────────────────────
//
// Hand-written: the generated client predates these routes. Every member may
// read; owners and admins (and an operator looking in) may save. Bookings,
// ticket response times and the chatbot read the same answer server-side.

/** The API's day codes, Monday first. */
export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Weekday = (typeof WEEKDAYS)[number];

/** One open slot on one day, in the organisation's time zone. Times are `HH:mm`. */
export interface BusinessHoursSlot {
  day: Weekday;
  opens: string;
  closes: string;
}

/** One day the business is shut, on top of its weekly hours. `date` is `YYYY-MM-DD`. */
export interface BusinessClosure {
  date: string;
  label: string;
}

export interface BusinessHours {
  /** IANA time zone, such as `Africa/Johannesburg`. */
  timezone: string;
  /** False until the hours are first saved; `weekly` is then Monday to Friday, 08:00 to 17:00. */
  configured: boolean;
  /** Slots ordered by day then opening time. A day with no slot is closed. */
  weekly: BusinessHoursSlot[];
  closures: BusinessClosure[];
}

/**
 * The body of `PUT /v1/organisations/me/hours`. It replaces everything.
 * Up to 6 slots a day, none overlapping; up to 100 closed days. A refused
 * save answers 400 with one of `INVALID_TIMEZONE`, `INVALID_DAY`,
 * `INVALID_TIME`, `OPENS_AFTER_CLOSES`, `OVERLAPPING_SLOTS`, `TOO_MANY_SLOTS`,
 * `TOO_MANY_CLOSURES`, `INVALID_DATE`, `LABEL_TOO_LONG` or `DUPLICATE_CLOSURE`.
 */
export interface SaveBusinessHoursRequest {
  /** Also changes the organisation's time zone. Omit to keep it. */
  timezone?: string;
  weekly: BusinessHoursSlot[];
  closures: BusinessClosure[];
}

/** `GET /v1/organisations/me/hours`. */
export function getBusinessHours(): Promise<Envelope<BusinessHours>> {
  return http.get<Envelope<BusinessHours>>('/v1/organisations/me/hours');
}

/** `PUT /v1/organisations/me/hours`: owner or admin. */
export function saveBusinessHours(req: SaveBusinessHoursRequest): Promise<Envelope<BusinessHours>> {
  return http.put<Envelope<BusinessHours>>('/v1/organisations/me/hours', { body: req });
}

// ── Teams (7 October 2026) ────────────────────────────────────────────────
//
// Named groups of the organisation's people with an optional lead. Every
// member may read; owners and admins may change. A deleted team disappears
// from the list.

/** The colours a team can carry: names from the console palette. */
export const TEAM_COLOURS = [
  'blue', 'indigo', 'violet', 'pink', 'rose', 'red', 'orange', 'amber', 'green', 'teal', 'cyan', 'gray'
] as const;
export type TeamColour = (typeof TEAM_COLOURS)[number];

export interface Team {
  id: string;
  name: string;
  description: string | null;
  lead_user_id: string | null;
  colour: TeamColour | null;
  member_user_ids: string[];
  member_count: number;
  created_at: string;
  updated_at: string;
}

export interface CreateTeamRequest {
  name: string;
  description?: string | null;
  /** Must be one of the organisation's people; the lead is added to the team. */
  lead_user_id?: string | null;
  colour?: TeamColour | null;
}

/** Only the fields sent change; `null` clears description, lead or colour. */
export type UpdateTeamRequest = Partial<CreateTeamRequest>;

/** `GET /v1/teams`. */
export function listTeams(): Promise<Envelope<Team[]> & { meta?: { count: number; colours: TeamColour[] } }> {
  return http.get<Envelope<Team[]> & { meta?: { count: number; colours: TeamColour[] } }>('/v1/teams');
}

/** `POST /v1/teams`: owner or admin. 409 `TEAM_NAME_TAKEN` when the name is in use. */
export function createTeam(req: CreateTeamRequest): Promise<Envelope<Team>> {
  return http.post<Envelope<Team>>('/v1/teams', { body: req });
}

/** `PATCH /v1/teams/{id}`: owner or admin. */
export function updateTeam(id: string, req: UpdateTeamRequest): Promise<Envelope<Team>> {
  return http.patch<Envelope<Team>>(`/v1/teams/${encodeURIComponent(id)}`, { body: req });
}

/** `DELETE /v1/teams/{id}`: owner or admin. */
export function deleteTeam(id: string): Promise<void> {
  return http.delete<void>(`/v1/teams/${encodeURIComponent(id)}`);
}

/**
 * `PUT /v1/teams/{id}/members`: owner or admin. Replaces the members; 400
 * `UNKNOWN_MEMBER` (with `user_ids`) when someone is not in the organisation.
 * A lead left out of the list stops being the lead.
 */
export function setTeamMembers(id: string, user_ids: string[]): Promise<Envelope<Team>> {
  return http.put<Envelope<Team>>(`/v1/teams/${encodeURIComponent(id)}/members`, { body: { user_ids } });
}
